/**
 * Affiliate Program — /business/affiliate
 * Dripify-style layout:
 *   1. Hero   — indigo gradient, headline + 3 USP pills + CTAs
 *   2. Stats strip — 4 program highlights (no pricing)
 *   3. How It Works — 5 horizontal step cards
 *   4. Benefits grid — 6 cards, flex wrap, 1160 px max-width, 24 px gap
 *   5. Promo Tools — what affiliates receive
 *   6. FAQ accordion
 *   7. Bottom CTA
 */

import { useState, useRef } from 'react';
import affiliateCtaImg from '../../../../assets/affiliate-cta.png';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import {
  ArrowRight,
  BarChart3,
  BookOpen,
  ChevronDown,
  CheckCircle2,
  DollarSign,
  ExternalLink,
  Gift,
  Globe,
  HeartHandshake,
  Image,
  Info,
  Link2,
  Lock,
  Megaphone,
  RefreshCw,
  Star,
  Tag,
  Trophy,
  Users,
  Zap,
} from 'lucide-react';
import { B2BMarketingPageLayout } from '../_shared/B2BMarketingPageLayout';

// ── animation helpers ─────────────────────────────────────────────────────────
function FadeUp({
  children,
  delay = 0,
  className = '',
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.48, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ── data ──────────────────────────────────────────────────────────────────────
const USP_LIST = [
  {
    title: 'No referral goes untracked',
    desc: 'Server-side tracking ensures every click and conversion is credited — no cookies required.',
    border: true,
  },
  {
    title: 'No minimum payout threshold',
    desc: 'Withdraw from your very first rupee earned. No waiting, no hoops to jump through.',
    border: false,
  },
  {
    title: 'Recurring income, month after month',
    desc: 'Earn a commission on every renewal — not just the first sale. Your income compounds as referrals stay subscribed.',
    border: true,
  },
];

const STATS = [
  { value: 'Recurring', label: 'Commission type', icon: RefreshCw },
  { value: 'Monthly', label: 'Payout frequency', icon: DollarSign },
  { value: 'No cap', label: 'Earnings limit', icon: Zap },
  { value: '30-day', label: 'Cookie window', icon: Lock },
];

const STEPS = [
  {
    n: '01',
    icon: HeartHandshake,
    iconBg: '#EDE9FE',
    iconColor: '#7C3AED',
    dotColor: '#7C3AED',
    title: 'Apply in minutes',
    desc: 'Submit a short registration form telling us about yourself and your audience. No lengthy paperwork required.',
  },
  {
    n: '02',
    icon: CheckCircle2,
    iconBg: '#DCFCE7',
    iconColor: '#16A34A',
    dotColor: '#16A34A',
    title: 'Get approved & onboarded',
    desc: "Once approved, you'll receive a welcome email with your login credentials and onboarding guide to get you up and running fast.",
  },
  {
    n: '03',
    icon: BarChart3,
    iconBg: '#DBEAFE',
    iconColor: '#2563EB',
    dotColor: '#2563EB',
    title: 'Access your affiliate dashboard',
    desc: 'Log into your GoGrabJob affiliate portal to grab your unique tracking link, download marketing materials, and explore strategy resources.',
  },
  {
    n: '04',
    icon: Megaphone,
    iconBg: '#FEF9C3',
    iconColor: '#CA8A04',
    dotColor: '#CA8A04',
    title: 'Promote to your network',
    desc: 'Share GoGrabJob with HR managers, recruiters, and hiring teams via LinkedIn, email newsletters, YouTube, or your blog.',
  },
  {
    n: '05',
    icon: DollarSign,
    iconBg: '#FFE4E6',
    iconColor: '#E11D48',
    dotColor: '#E11D48',
    title: 'Earn recurring commissions',
    desc: 'Get paid every month for every active subscription your referrals maintain — no ceiling, no expiry, no resets.',
  },
];

const BENEFITS = [
  {
    icon: Globe,
    cardBg: '#F5F3FF',
    iconBg: '#DDD6FE',
    iconColor: '#6D28D9',
    title: 'Free Affiliate Dashboard',
    desc: 'Your own portal with real-time stats, unique tracking links, banners, and coupon codes — completely free, forever.',
  },
  {
    icon: Trophy,
    cardBg: '#FFF8ED',
    iconBg: '#FDE68A',
    iconColor: '#B45309',
    title: 'Recurring Commission Model',
    desc: 'Earn on every first purchase and every renewal. Your passive income grows as long as your referrals stay subscribed.',
  },
  {
    icon: Star,
    cardBg: '#F0FFF8',
    iconBg: '#BBF7D0',
    iconColor: '#15803D',
    title: 'Promote a Platform Teams Trust',
    desc: 'GoGrabJob is used by hiring teams across industries. A trusted brand means higher conversions and fewer objections.',
  },
  {
    icon: BookOpen,
    cardBg: '#F0F9FF',
    iconBg: '#BAE6FD',
    iconColor: '#0369A1',
    title: 'Free Training & Growth Resources',
    desc: 'Video tutorials, strategy articles, and affiliate marketing guides inside your dashboard — built to help you scale faster.',
  },
  {
    icon: Lock,
    cardBg: '#FFF1F2',
    iconBg: '#FECDD3',
    iconColor: '#BE123C',
    title: 'Cookie-Free Reliable Tracking',
    desc: 'Server-side attribution means every referral is credited regardless of browser, device, or ad-blocker status.',
  },
  {
    icon: Gift,
    cardBg: '#EEF2FF',
    iconBg: '#C7D2FE',
    iconColor: '#4338CA',
    title: 'Ready-Made Promo Materials',
    desc: 'Professionally designed banners, text links, and exclusive coupon codes so you can start promoting from day one.',
  },
];


const FAQS = [
  {
    q: 'How do I become a GoGrabJob affiliate?',
    a: "Fill out the registration form on this page. Our team reviews every application and, once approved, you'll receive a welcome email with your dashboard login and everything you need to get started.",
  },
  {
    q: 'How are my referrals tracked? Will I always get credited?',
    a: "GoGrabJob uses server-side tracking combined with unique affiliate links — no reliance on cookies or browser extensions. Every click and conversion is automatically recorded against your account.<br><br><strong>Please note:</strong> Self-referrals and referrals for your own team members do not qualify for commission. Contact our support team if you'd like to purchase a plan for your organisation at a discounted rate.",
  },
  {
    q: 'When and how are commissions paid?',
    a: 'Commissions are paid out monthly with no minimum withdrawal threshold. Payments are sent directly to your preferred method — bank transfer or PayPal — as soon as the earning period closes.',
  },
  {
    q: 'Which channels can I use to promote GoGrabJob?',
    a: 'We recommend a multi-channel approach for the best results. Our top affiliates use LinkedIn outreach, personalised email newsletters, YouTube demo videos, blog posts, and community forums such as Reddit and Quora. The more channels you use, the faster your audience grows.',
  },
  {
    q: 'What is my affiliate link and where do I find it?',
    a: "Your affiliate link is a unique tracking URL that directs prospects to the GoGrabJob website while recording the referral under your account. You'll find it in your affiliate dashboard immediately after your first login.",
  },
  {
    q: 'Can I run paid ads to promote GoGrabJob?',
    a: 'Paid advertising for GoGrabJob through any ad network (Google Ads, Meta, LinkedIn Ads, etc.) is strictly prohibited. Violations will result in immediate account closure and forfeiture of pending commissions. <a href="/business/affiliate/terms" target="_blank" class="font-medium underline" style="color:#7760F9">Read the full terms</a>',
  },
  {
    q: 'Is training provided for new affiliates?',
    a: "Yes. Your dashboard includes video tutorials, strategy guides, and advanced techniques contributed by our top-performing affiliates — everything you need whether you're new to affiliate marketing or looking to scale.",
  },
];

// ── purple checkmark SVG (matches Dripify's inline SVG exactly) ───────────────
function PurpleCheck() {
  return (
    <svg
      style={{ minWidth: 22 }}
      width="22"
      height="22"
      viewBox="0 0 22 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <circle cx="11" cy="11" r="11" fill="#7760F9" />
      <path
        d="M16.1667 7.79169L9.75004 14.2084L6.83337 11.2917"
        stroke="white"
        strokeWidth="1.05"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ── affiliate signup form ─────────────────────────────────────────────────────
function AffiliateForm() {
  const [fields, setFields] = useState({
    firstName: '',
    lastName: '',
    email: '',
    linkedinUrl: '',
    affiliateId: '',
    acceptMarketing: false,
    acceptTerms: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const set = (k: keyof typeof fields, v: string | boolean) =>
    setFields((prev) => ({ ...prev, [k]: v }));

  const canSubmit =
    fields.firstName.trim() &&
    fields.lastName.trim() &&
    fields.email.trim() &&
    fields.linkedinUrl.trim() &&
    fields.affiliateId.trim() &&
    fields.acceptTerms;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 900));
    setSubmitting(false);
    setSubmitted(true);
  }

  const inputCls =
    'w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-800 placeholder-gray-400 outline-none transition focus:border-[#7760F9] focus:ring-2 focus:ring-[#7760F9]/20 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100 dark:placeholder-zinc-500';

  if (submitted) {
    return (
      <div className="flex flex-col items-center gap-4 py-10 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#F2F0FF]">
          <CheckCircle2 className="h-7 w-7 text-[#7760F9]" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-white">
          Application received!
        </h3>
        <p className="text-sm text-gray-500 dark:text-zinc-400">
          We'll review your application and send your onboarding details to{' '}
          <strong>{fields.email}</strong> within 1–2 business days.
        </p>
        <a
          href="https://dash.partnerstack.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#7760F9] hover:underline"
        >
          Already approved? Log in to your dashboard
          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-3.5">
      {/* first name — full-width separate row, matching Dripify screenshot */}
      <input
        type="text"
        placeholder="First name*"
        required
        maxLength={40}
        value={fields.firstName}
        onChange={(e) => set('firstName', e.target.value)}
        className={inputCls}
      />
      {/* last name — full-width separate row */}
      <input
        type="text"
        placeholder="Last name*"
        required
        maxLength={40}
        value={fields.lastName}
        onChange={(e) => set('lastName', e.target.value)}
        className={inputCls}
      />

      <input
        type="email"
        placeholder="Business email*"
        required
        maxLength={200}
        value={fields.email}
        onChange={(e) => set('email', e.target.value)}
        className={inputCls}
      />

      <input
        type="url"
        placeholder="LinkedIn Profile URL*"
        required
        maxLength={300}
        value={fields.linkedinUrl}
        onChange={(e) => set('linkedinUrl', e.target.value)}
        className={inputCls}
      />

      {/* affiliate ID with domain prefix + tooltip */}
      <div className="relative flex overflow-hidden rounded-lg border border-gray-200 bg-white focus-within:border-[#7760F9] focus-within:ring-2 focus-within:ring-[#7760F9]/20 dark:border-zinc-600 dark:bg-zinc-800">
        <span className="flex shrink-0 items-center border-r border-gray-200 bg-gray-50 px-3 text-xs text-gray-500 dark:border-zinc-700 dark:bg-zinc-700 dark:text-zinc-400">
          gograbjob.com/ref/
        </span>
        <input
          type="text"
          placeholder="Affiliate ID*"
          required
          maxLength={60}
          value={fields.affiliateId}
          onChange={(e) => set('affiliateId', e.target.value)}
          className="min-w-0 flex-1 bg-transparent px-3 py-3 text-sm text-gray-800 placeholder-gray-400 outline-none dark:text-zinc-100 dark:placeholder-zinc-500"
        />
        {/* tooltip trigger */}
        <div className="relative flex items-center pr-3" ref={tooltipRef}>
          <button
            type="button"
            aria-label="What is Affiliate ID?"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onFocus={() => setShowTooltip(true)}
            onBlur={() => setShowTooltip(false)}
            className="text-gray-400 hover:text-[#7760F9]"
          >
            <Info className="h-4.5 w-4.5 h-[18px] w-[18px]" aria-hidden />
          </button>
          <AnimatePresence>
            {showTooltip && (
              <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4 }}
                transition={{ duration: 0.15 }}
                className="absolute bottom-full right-0 mb-2 w-56 rounded-lg bg-gray-800 px-3 py-2 text-xs leading-relaxed text-white shadow-lg dark:bg-zinc-700"
                role="tooltip"
              >
                Enter a unique phrase you'd like included in your affiliate link.
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* checkboxes */}
      <label className="flex cursor-pointer items-start gap-2.5 text-xs text-gray-500 dark:text-zinc-400">
        <input
          type="checkbox"
          checked={fields.acceptMarketing}
          onChange={(e) => set('acceptMarketing', e.target.checked)}
          className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-[#7760F9]"
        />
        By checking this box, you agree to receive updates on the GoGrabJob Affiliate Program,
        including commissions, promotions, and bonuses.
      </label>
      <label className="flex cursor-pointer items-start gap-2.5 text-xs text-gray-500 dark:text-zinc-400">
        <input
          type="checkbox"
          required
          checked={fields.acceptTerms}
          onChange={(e) => set('acceptTerms', e.target.checked)}
          className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-[#7760F9]"
        />
        By checking this box, you agree to the{' '}
        <a
          href="/business/affiliate/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-[#7760F9] hover:underline"
        >
          Affiliate Terms
        </a>{' '}
        and{' '}
        <a
          href="/business/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-[#7760F9] hover:underline"
        >
          Privacy Policy
        </a>
        .
      </label>

      <button
        type="submit"
        disabled={!canSubmit || submitting}
        className="mt-1.5 flex w-full items-center justify-center rounded-lg bg-[#7760F9] py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#6349F8] disabled:cursor-not-allowed disabled:opacity-50"
      >
        {submitting ? (
          <span className="flex items-center gap-2">
            <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden>
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
            </svg>
            Submitting…
          </span>
        ) : (
          'Partner and earn'
        )}
      </button>

      <p className="text-center text-xs text-gray-400 dark:text-zinc-500">
        Already a GoGrabJob Partner?{' '}
        <a
          href="https://dash.partnerstack.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-[#7760F9] hover:underline"
        >
          Log in
        </a>
      </p>
    </form>
  );
}

// ── sub-components ────────────────────────────────────────────────────────────
function FaqItem({
  q,
  a,
  index,
  defaultOpen = false,
}: {
  q: string;
  a: string;
  index: number;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <FadeUp delay={0.04 * index}>
      <div className="rounded-2xl border border-gray-200 bg-gray-100 dark:border-zinc-700 dark:bg-zinc-800/60">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex w-full items-center gap-4 px-5 py-5 text-left"
        >
          <span className="flex-1 text-base font-medium text-slate-900 dark:text-zinc-100 sm:text-[17px]">
            {q}
          </span>
          {/* rotating chevron */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            aria-hidden
            className={`ml-auto h-3 w-3 shrink-0 fill-current text-slate-500 transition-transform duration-300 dark:text-zinc-400 ${
              open ? '-rotate-180' : '-rotate-90'
            }`}
          >
            <path
              fillRule="evenodd"
              d="M11.99997 18.1669a2.38 2.38 0 0 1-1.68266-.69733l-9.52-9.52a2.38 2.38 0 1 1 3.36532-3.36532l7.83734 7.83734 7.83734-7.83734a2.38 2.38 0 1 1 3.36532 3.36532l-9.52 9.52a2.38 2.38 0 0 1-1.68266.69734z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key="body"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.26 }}
              className="overflow-hidden"
            >
              <div
                className="px-6 pb-5 text-[15px] leading-relaxed text-slate-600 dark:text-zinc-300"
                dangerouslySetInnerHTML={{ __html: a }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </FadeUp>
  );
}

// ── vertical timeline step ────────────────────────────────────────────────────
function TimelineStep({
  n,
  icon: Icon,
  iconBg,
  iconColor,
  dotColor,
  title,
  desc,
  isFirst,
  isLast,
}: {
  n: string;
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  dotColor: string;
  title: string;
  desc: string;
  isFirst: boolean;
  isLast: boolean;
}) {
  return (
    <div className="flex min-h-[140px] items-stretch">

      {/* ── LEFT: coloured icon box (desktop only) ───────── */}
      <div className="hidden w-[38%] shrink-0 items-center justify-center sm:flex">
        <div
          className="flex h-20 w-20 items-center justify-center rounded-2xl"
          style={{ background: iconBg }}
        >
          <Icon className="h-8 w-8" style={{ color: iconColor }} aria-hidden />
        </div>
      </div>

      {/* ── CENTER: static line + coloured dot ──────────── */}
      <div className="relative flex w-10 shrink-0 flex-col items-center">
        <div className={`w-px flex-1 bg-gray-200 dark:bg-zinc-700 ${isFirst ? 'opacity-0' : ''}`} />
        <div
          className="relative z-10 h-3.5 w-3.5 shrink-0 rounded-full border-2 border-white shadow-sm dark:border-zinc-900"
          style={{ background: dotColor }}
        />
        <div className={`w-px flex-1 bg-gray-200 dark:bg-zinc-700 ${isLast ? 'opacity-0' : ''}`} />
      </div>

      {/* ── RIGHT: text ──────────────────────────────────── */}
      <div className="flex flex-1 flex-col justify-center py-8 pl-6 sm:pl-8">
        <h4 className="text-base font-bold text-gray-900 dark:text-white sm:text-lg">{title}</h4>
        <p className="mt-1.5 text-base leading-relaxed text-gray-500 dark:text-zinc-400">{desc}</p>
      </div>

    </div>
  );
}

// ── page ──────────────────────────────────────────────────────────────────────
export function AffiliatePage() {
  return (
    <B2BMarketingPageLayout>
      {/*
        White page shell — the layout's lavender gradient blends with the
        hero card's #F2F0FF background, hiding the rounded corners.
        This white wrapper sits on top of the gradient and gives the lavender
        hero card clear contrast so the curves are visible.
      */}
      <div
        className="dark:bg-zinc-950"
        style={{
          background:
            'linear-gradient(160deg, #f8f7ff 0%, #ffffff 30%, #f0f9ff 60%, #faf5ff 85%, #f0fdf4 100%)',
        }}
      >

      {/* ══════════════════════════════════════════════════════
          1. HERO — lavender card, mirroring FeaturesPage card pattern:
             mx-2 mt-2 rounded-2xl sm:mx-4 sm:rounded-3xl lg:mx-6 lg:rounded-[2rem]
      ══════════════════════════════════════════════════════ */}
      <section
        id="hero"
        className="relative mx-2 mt-2 overflow-hidden rounded-2xl sm:mx-4 sm:rounded-3xl lg:mx-6 lg:rounded-[2rem]"
        style={{ background: 'rgb(242, 240, 255)' }}
      >
        {/* decorative blob */}
        <svg
          viewBox="0 0 711 318"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="pointer-events-none absolute bottom-0 left-0 z-0 w-[55%] opacity-60"
          aria-hidden
        >
          <path
            opacity="0.06"
            d="M377.166 18.4544L667.407 224.297C684.034 235.041 696.883 250.716 704.158 269.131C711.433 287.547 712.77 307.78 707.981 327.003C703.193 346.225 692.518 363.475 677.451 376.339C662.384 389.203 643.677 397.038 623.948 398.748L111.526 466.207C92.0757 469.665 72.0363 466.973 54.1973 458.505C36.3584 450.037 21.6074 436.214 11.9978 418.961C2.38818 401.708 -1.60192 381.883 0.582913 362.246C2.76775 342.608 11.0188 324.136 24.1875 309.4L245.501 37.2297C260.637 17.4871 282.905 4.462 307.523 0.951553C332.14 -2.55889 357.145 3.72523 377.166 18.4544Z"
            fill="#7760F9"
          />
        </svg>

        {/* inner two-column layout: left = copy, right = form (right-aligned) */}
        <div className="relative z-10 mx-auto flex min-h-[640px] max-w-[1160px] flex-col items-start gap-10 px-5 py-14 sm:px-8 sm:py-16 md:px-10 lg:min-h-[680px] lg:flex-row lg:items-center lg:justify-between lg:gap-12 lg:py-20">

          {/* ── LEFT — headline + USPs ───────────────────────── */}
          <motion.div
            className="w-full max-w-[440px] shrink-0"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl font-extrabold leading-tight tracking-tight text-gray-900 sm:text-5xl md:text-[3.25rem]">
              Refer once.
              <br />
              <span className="text-[#6349F8]">Earn every month.</span>
            </h1>

            <p className="mt-4 text-base leading-relaxed text-gray-600 dark:text-zinc-400">
              Introduce HR teams and recruiters to GoGrabJob — India's hiring platform built for speed and scale.
              Earn a recurring commission on every subscription, with no cap and no expiry.
            </p>

            <div className="mt-6 flex flex-col">
              {USP_LIST.map(({ title, desc }, i) => (
                <div
                  key={title}
                  className={`flex items-start gap-3 py-4 border-b border-dashed border-gray-300 ${
                    i === 0 ? 'border-t border-dashed border-gray-300' : ''
                  }`}
                >
                  <PurpleCheck />
                  <div>
                    <p className="text-sm font-semibold text-gray-800 dark:text-white">{title}</p>
                    <p className="mt-0.5 text-sm leading-relaxed text-gray-500 dark:text-zinc-400">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* ── RIGHT — form card, right-aligned ────────────── */}
          <motion.div
            id="affiliate-form"
            className="w-full scroll-mt-24 rounded-2xl border border-gray-200 bg-white px-6 py-8 shadow-lg dark:border-zinc-700 dark:bg-zinc-900 sm:px-8 sm:py-9 lg:w-auto lg:min-w-[400px] lg:max-w-[460px] lg:self-start"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.12 }}
          >
            <h2 className="mb-6 text-center text-lg font-bold text-gray-900 dark:text-white sm:text-xl">
              Join the GoGrabJob Affiliate Program
            </h2>
            <AffiliateForm />
          </motion.div>

        </div>
      </section>


      {/* ══════════════════════════════════════════════════════
          3. COMMISSION STRUCTURE — two cards (monthly + annual)
             Matches Dripify's #commission section layout exactly
      ══════════════════════════════════════════════════════ */}
      <section id="commission" className="scroll-mt-24 px-4 py-16 sm:px-6 md:px-8 md:py-20">
        <div className="mx-auto max-w-[1160px]">

          <FadeUp className="text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
              Commission Structure
            </h2>
            <p className="mx-auto mt-3 max-w-lg text-base leading-relaxed text-gray-500 dark:text-zinc-400">
              Refer hiring teams to GoGrabJob and earn a generous recurring commission — on the first purchase and every renewal that follows.
            </p>
          </FadeUp>

          <div className="mt-10 grid gap-6 sm:grid-cols-2">

            {/* ── Monthly card ─── */}
            <FadeUp delay={0.06}>
              <div className="rounded-2xl p-7 sm:p-9" style={{ background: '#EBF5FF' }}>
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold text-gray-900">
                    Monthly Subscription
                  </h4>
                  <span className="text-2xl" role="img" aria-label="calendar">📅</span>
                </div>

                <div className="mt-6 flex gap-10">
                  <div>
                    <p className="text-5xl font-extrabold tracking-tight text-gray-900 sm:text-6xl">35%</p>
                    <p className="mt-2 text-xs text-gray-500">First purchase</p>
                  </div>
                  <div>
                    <p className="text-5xl font-extrabold tracking-tight text-gray-900 sm:text-6xl">20%</p>
                    <p className="mt-2 text-xs text-gray-500">
                      Renewal<br />(all upcoming payments)
                    </p>
                  </div>
                </div>
              </div>
            </FadeUp>

            {/* ── Annual card ─── */}
            <FadeUp delay={0.12}>
              <div className="rounded-2xl p-7 sm:p-9" style={{ background: '#FFEFEB' }}>
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold text-gray-900">
                    Annual Subscription
                  </h4>
                  <div className="flex gap-1 text-xl" aria-hidden>
                    <span>❄️</span><span>🌿</span><span>☀️</span><span>🍂</span>
                  </div>
                </div>

                <div className="mt-6 flex gap-10">
                  <div>
                    <p className="text-5xl font-extrabold tracking-tight text-gray-900 sm:text-6xl">30%</p>
                    <p className="mt-2 text-xs text-gray-500">First purchase</p>
                  </div>
                  <div>
                    <p className="text-5xl font-extrabold tracking-tight text-gray-900 sm:text-6xl">30%</p>
                    <p className="mt-2 text-xs text-gray-500">
                      Renewal<br />(all upcoming payments)
                    </p>
                  </div>
                </div>
              </div>
            </FadeUp>

          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          4. HOW IT WORKS — Dripify sticky-cards stacking timeline
             Each card uses position:sticky with a deeper top offset
             so newer cards slide over older ones as you scroll,
             exactly matching the Dripify "sticky-cards-wrapper" pattern.
      ══════════════════════════════════════════════════════ */}
      <section id="how-it-works" className="scroll-mt-24 px-4 py-16 sm:px-6 md:px-8 md:py-20">
        <div className="mx-auto max-w-[860px]">

        <FadeUp>
          <h2 className="text-center text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
            Getting Started Is Simple
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-center text-base leading-relaxed text-gray-500 dark:text-zinc-400">
            From application to your first commission — the whole process takes less than a week.
          </p>
        </FadeUp>

          <div className="mt-8">
            {STEPS.map(({ n, icon, iconBg, iconColor, dotColor, title, desc }, i) => (
              <TimelineStep
                key={n}
                n={n}
                icon={icon}
                iconBg={iconBg}
                iconColor={iconColor}
                dotColor={dotColor}
                title={title}
                desc={desc}
                isFirst={i === 0}
                isLast={i === STEPS.length - 1}
              />
            ))}
          </div>

        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          4. BENEFITS GRID — 6 cards, flex wrap, 1160px / 24px gap
      ══════════════════════════════════════════════════════ */}
      <section id="benefits" className="scroll-mt-24 border-y border-gray-100 bg-white/60 px-4 py-16 dark:border-zinc-800 dark:bg-zinc-900/40 sm:px-6 md:px-8 md:py-20">
        <div className="mx-auto max-w-[1160px]">
          <FadeUp className="text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.13em] text-indigo-600 dark:text-indigo-400">
              Why Join Us
            </p>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
              Reasons to Become a GoGrabJob Affiliate
            </h2>
            <p className="mx-auto mt-3 max-w-lg text-base leading-relaxed text-gray-500 dark:text-zinc-400">
              Everything you need to promote confidently, track accurately, and earn consistently.
            </p>
          </FadeUp>

          {/* flex wrap, justify-between, gap-6 — matches computed styles shared */}
          <div className="mt-10 flex flex-wrap justify-between gap-6">
            {BENEFITS.map(({ icon: Icon, cardBg, iconBg, iconColor, title, desc }, i) => (
              <FadeUp
                key={title}
                delay={0.05 * i}
                className="w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]"
              >
                <div
                  className="flex h-full flex-col rounded-2xl border border-transparent p-6 dark:border-zinc-700/40 dark:bg-zinc-800/60"
                  style={{ background: cardBg }}
                >
                  <div
                    className="flex h-11 w-11 items-center justify-center rounded-xl"
                    style={{ background: iconBg }}
                  >
                    <Icon className="h-5 w-5" style={{ color: iconColor }} aria-hidden />
                  </div>
                  <h3 className="mt-4 text-base font-bold text-gray-900 dark:text-white">
                    {title}
                  </h3>
                  <p className="mt-2 flex-1 text-base leading-relaxed text-gray-600 dark:text-zinc-400">
                    {desc}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>


      {/* ══════════════════════════════════════════════════════
          6. FAQ ACCORDION
      ══════════════════════════════════════════════════════ */}
      <section
        id="faq"
        className="scroll-mt-24 px-4 py-16 sm:px-6 md:px-8 md:py-20"
      >
        <div className="mx-auto max-w-3xl">
          <FadeUp className="mb-10 text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
              Frequently Asked Questions
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-gray-500 dark:text-zinc-400">
              Everything you need to know about joining and succeeding in the GoGrabJob Affiliate Program.
            </p>
          </FadeUp>

          <div className="space-y-4">
            {FAQS.map((item, i) => (
              <FaqItem
                key={item.q}
                q={item.q}
                a={item.a}
                index={i}
                defaultOpen={false}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          7. BOTTOM CTA — Dripify style
             id="cta-affiliate"
             Background: #6349F8 (primarydark), rounded-2xl
             Left col: affiliate person SVG illustration
             Right col: h3 headline + outline CTA button
      ══════════════════════════════════════════════════════ */}
      <section id="apply" className="scroll-mt-24 px-2 pb-16 sm:px-4 md:pb-20">
        <FadeUp>
          <div
            id="cta-affiliate"
            className="mx-auto overflow-hidden rounded-3xl"
            style={{ background: '#6349F8' }}
          >
            <div className="flex flex-col lg:flex-row lg:items-stretch">

              {/* ── LEFT: image panel ── */}
              <div
                className="relative w-full shrink-0 overflow-hidden rounded-3xl lg:w-[50%] lg:rounded-r-none"
                style={{ background: '#7B68EE' }}
              >
                <img
                  src={affiliateCtaImg}
                  alt="Affiliate ambassador illustration"
                  className="absolute inset-0 h-full w-full object-cover object-center"
                />
                {/* spacer keeps the panel height consistent with right column */}
                <div className="invisible py-10 lg:py-12" aria-hidden />
              </div>

              {/* ── RIGHT: headline + button ── */}
              <div className="flex flex-1 flex-col items-end justify-center gap-4 px-8 py-10 text-right lg:px-12 lg:py-12">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/70">
                  GoGrabJob Affiliate Program
                </p>
                <h2 className="text-2xl font-bold leading-snug tracking-tight text-white sm:text-3xl">
                  Turn your network<br />
                  into a recurring<br />
                  revenue stream
                </h2>
                <a
                  href="#affiliate-form"
                  className="inline-flex items-center gap-2 rounded-xl border-2 border-white px-7 py-3 text-sm font-bold text-white transition-all hover:bg-white hover:text-[#6349F8]"
                >
                  Join the program
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </a>
              </div>

            </div>
          </div>
        </FadeUp>
      </section>

      </div>{/* end white page shell */}
    </B2BMarketingPageLayout>
  );
}
